import { tryCatch } from '@/try-catch';
import {
  keepPreviousData,
  queryOptions,
  useIsMutating,
  useMutationState,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useParams } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { api } from '@/api';
import {
  Document,
  DocumentCheckResponse,
  DocumentVersionsResponse,
  GetProxyIndexedDocumentsStatusResponse,
  KgReason,
  KgStatusResponse,
  UploadCapabilities,
} from '@/features/documents/types';
import {
  computeSha256,
  documentIndexingLiveUpdate,
  getIndexingState,
  isActiveKgReason,
  isKgDocumentType,
  KgEvaluationStatus,
  stagedFingerprint,
  type DocumentIndexingSnapshot,
} from '@/features/documents/utils';
import {
  DOCUMENT_TYPE,
  type DocumentTypeName,
  STALL_POLL_INTERVAL_MS,
} from '@/features/documents/constants';
import { queryStream } from '@/features/workspaces/stream';
import { DeferredSharePointItem } from '@/features/sharepoint/types';
import { DeferredProcoreItem } from '@/features/procore/types';

export const entityDocumentsQuery = (resourceId: string, documentType: DocumentTypeName) =>
  queryOptions({
    queryKey: ['entityDocuments', resourceId, documentType],
    queryFn: async () =>
      (
        await api.get<Document[]>('v1/documents', {
          params: {
            type: DOCUMENT_TYPE[documentType],
            resourceId,
            orderBy: 'FileName',
            orderDirection: 'asc',
          },
        })
      ).data,
  });

export const documentQuery = ({
  documentId,
  organizationId,
  projectId,
  allowOrganizationFallback = true,
}: {
  documentId: string;
  organizationId: string;
  projectId?: string;
  allowOrganizationFallback?: boolean;
}) =>
  queryOptions({
    queryKey: ['document', documentId, organizationId, projectId, allowOrganizationFallback],
    queryFn: async () => {
      if (projectId) {
        const { data, error } = await tryCatch(
          api.get<Document>(
            `v1/documents/organization/${organizationId}/project/${projectId}/document/${documentId}`,
          ),
        );
        if (!error) return data.data;
        const is404 = error instanceof AxiosError && error.response?.status === 404;
        if (!is404 || !allowOrganizationFallback) throw error;
      }
      return (
        await api.get<Document>(
          `v1/documents/organization/${organizationId}/document/${documentId}`,
        )
      ).data;
    },
  });

export function useDocument(documentId: string | null | undefined, projectScope?: string) {
  const auth = useAuth();
  const { projectId } = useParams({ strict: false });

  return useQuery({
    ...documentQuery({
      documentId: documentId ?? '',
      organizationId: auth.user.organizationId,
      projectId: projectScope ?? projectId,
      allowOrganizationFallback: !projectScope,
    }),
    enabled: !!documentId,
  });
}

export function useFetchDocument() {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const { projectId } = useParams({ strict: false });

  return (documentId: string) =>
    queryClient.query(
      documentQuery({ documentId, organizationId: auth.user.organizationId, projectId }),
    );
}

export const indexingStatusQuery = (
  documentType: DocumentTypeName,
  resourceId: string,
  { streamEnabled = true }: { streamEnabled?: boolean } = {},
) => {
  const evalType = DOCUMENT_TYPE[documentType];
  const queryKey = ['indexingStatus', documentType, resourceId];
  const invalidate = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
    queryClient.invalidateQueries(entityDocumentsQuery(resourceId, documentType), {
      cancelRefetch: false,
    });
    if (isKgDocumentType(documentType))
      queryClient.invalidateQueries(kgStatusQuery(resourceId, documentType), {
        cancelRefetch: false,
      });
  };
  return queryOptions({
    queryKey,
    queryFn: async () =>
      (
        await api.get<GetProxyIndexedDocumentsStatusResponse>('v1/documents/indexing_status', {
          params: { type: evalType, evalId: resourceId },
        })
      ).data,
    refetchInterval: (query) =>
      query.state.data?.analysisTriggered &&
      !query.state.data.isStalled &&
      !getIndexingState(query.state.data, { waitForKg: true }).isIndexed
        ? STALL_POLL_INTERVAL_MS
        : false,
    meta: {
      streamEnabled,
      stream:
        resourceId && documentType !== 'project'
          ? queryStream<GetProxyIndexedDocumentsStatusResponse, DocumentIndexingSnapshot>({
              topic: 'document_indexing',
              type: evalType,
              resourceId,
              isActive: (data) => !getIndexingState(data, { waitForKg: true }).isIndexed,
              onReconnect: invalidate,
              onStatusUpdate: (payload, queryClient) => {
                const listed = queryClient.getQueryData<Document[]>(
                  entityDocumentsQuery(resourceId, documentType).queryKey,
                );
                const action = documentIndexingLiveUpdate(
                  payload,
                  queryClient.getQueryData<GetProxyIndexedDocumentsStatusResponse>(queryKey),
                  listed?.filter((doc) => doc.indexingError && !doc.isIndexed).length,
                );
                if (action === 'nudge') return invalidate(queryClient);

                queryClient.setQueryData<GetProxyIndexedDocumentsStatusResponse>(
                  queryKey,
                  (old) => {
                    if (!old) return;
                    const updated = action === 'skip' ? old : { ...old, ...action };
                    const kg = payload.kg;
                    if (!kg || kg.reason === null) return updated;
                    const isIndexing = kg.reason === 'indexing';
                    const isBuilding = isIndexing || kg.reason === 'building';
                    return {
                      ...updated,
                      kgStatus: isBuilding
                        ? KgEvaluationStatus.InProgress
                        : (kg.status ?? old.kgStatus),
                      kgProgress: isIndexing ? 0 : (kg.progress ?? old.kgProgress),
                      isStalled: false,
                    };
                  },
                );
                if (action !== 'skip') return invalidate(queryClient);
              },
            })
          : undefined,
    },
  });
};

export const DOCUMENT_VERSIONS_QUERY_KEY = 'documentVersions';

export const documentVersionsQuery = (docId: string) =>
  queryOptions({
    queryKey: [DOCUMENT_VERSIONS_QUERY_KEY, docId],
    queryFn: async () =>
      (await api.get<DocumentVersionsResponse>(`v1/versioning/documents/${docId}/versions`)).data,
  });

export const documentCheckQuery = ({
  resourceId,
  resourceType,
  files,
  sharepointItems,
  procoreItems,
}: {
  resourceId: string;
  resourceType: DocumentTypeName;
  files: File[];
  sharepointItems: DeferredSharePointItem[];
  procoreItems: DeferredProcoreItem[];
}) =>
  queryOptions({
    queryKey: [
      'documentCheck',
      resourceType,
      resourceId,
      stagedFingerprint(files, sharepointItems, procoreItems),
    ],
    queryFn: async ({ signal }) => {
      const fileHashes: string[] = [];
      for (const file of files) fileHashes.push(await computeSha256(file));
      return (
        await api.post<DocumentCheckResponse>(
          `v1/documents/check?type=${DOCUMENT_TYPE[resourceType]}&resourceId=${resourceId}`,
          {
            filenames: [
              ...files.map((f) => f.name),
              ...sharepointItems.map((item) => item.name || item.itemId),
              ...procoreItems.map((item) => item.name || item.itemId),
            ],
            fileHashes: fileHashes.length > 0 ? fileHashes : undefined,
            fileSizes: [
              ...files.map((f) => f.size),
              ...sharepointItems.map((item) => item.size ?? 0),
              ...procoreItems.map((item) => item.size ?? 0),
            ],
          },
          { signal },
        )
      ).data;
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

export function useDocumentsIndexingStatus({
  documentType,
  id,
  enabled = true,
  waitForKg = false,
}: {
  documentType: DocumentTypeName;
  id: string;
  enabled?: boolean;
  waitForKg?: boolean;
}) {
  const { data: documents, isSuccess: documentsLoaded } = useQuery({
    ...entityDocumentsQuery(id, documentType),
    enabled,
  });
  const hasDocuments = enabled && documentsLoaded && documents.length > 0;
  const query = useQuery({
    ...indexingStatusQuery(documentType, id),
    enabled: hasDocuments,
  });
  const {
    progress: indexingProgress,
    isIndexed,
    docsResolved,
    failedCount: failedDocumentsCount,
    kgFailed,
  } = getIndexingState(hasDocuments ? query.data : undefined, { waitForKg });
  const hasNoDocuments = documentsLoaded && documents.length === 0;
  const areDocumentsIndexed = hasNoDocuments || (hasDocuments && isIndexed);

  const analysisMutationStatuses = useMutationState({
    filters: {
      mutationKey: ['analyseDocuments', documentType],
      predicate: (mutation) => mutation.state.variables === id,
    },
    select: (mutation) => mutation.state.status,
  });
  const pendingUploads = useIsMutating({
    mutationKey: ['uploadDocuments', documentType],
    predicate: (mutation) => (mutation.state.variables as { resourceId: string }).resourceId === id,
  });
  const analysisTriggered =
    !!query.data?.analysisTriggered ||
    pendingUploads > 0 ||
    analysisMutationStatuses.some((status) => status !== 'error');

  return {
    ...query,
    areDocumentsIndexed,
    indexingProgress,
    isIndexingStalled: query.data?.isStalled ?? false,
    hasNoDocuments,
    failedDocumentsCount,
    kgFailed,
    analysisTriggered,
    analysisInProgress: hasDocuments && !isIndexed && analysisTriggered,
    isKgBuilding: hasDocuments && docsResolved && !isIndexed,
  };
}

export const uploadCapabilitiesQuery = () =>
  queryOptions({
    queryKey: ['uploadCapabilities'],
    queryFn: async () => (await api.get<UploadCapabilities>('v1/uploads/capabilities')).data,
    staleTime: 60_000,
  });

export const kgStatusQuery = (resourceId: string, documentType: DocumentTypeName) => {
  const queryKey = ['kgStatus', resourceId];
  return queryOptions({
    queryKey,
    queryFn: async () =>
      (await api.get<KgStatusResponse>(`v1/evaluations/${resourceId}/kg-status`)).data,
    meta: {
      stream: queryStream<
        KgStatusResponse,
        {
          status: number | null;
          progress?: number | null;
          reason?: KgReason | null;
          wp_ready?: boolean | null;
        }
      >({
        topic: 'kg_evaluation',
        type: DOCUMENT_TYPE[documentType],
        resourceId,
        isActive: (data) => isActiveKgReason(data?.reason),
        onStatusUpdate: (payload, queryClient) => {
          if (payload.reason == null)
            return queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });

          const cached = queryClient.getQueryData<KgStatusResponse>(queryKey);
          queryClient.setQueryData<KgStatusResponse>(queryKey, {
            status: payload.status,
            progress: payload.progress ?? 0,
            reason: payload.reason,
            wpReady: payload.wp_ready ?? cached?.wpReady ?? false,
          });
        },
      }),
    },
  });
};
